import { tokenStructure } from '@/api/refresh_token';
import Pusher from 'pusher-js';

const { VUE_APP_PUSHER_API_KEY, VUE_APP_PUSHER_AUTH_ENDPOINT, VUE_APP_PUSHER_CLUSTER } = process.env;

/* Private */

const applyFn = (fn) => {
	// Validates that the service runs only on relevant environments
	// and handles exceptions - we cannot fail because of external services\libraries
	try {
		return fn();
	} catch (err) {
		console.error(err);
		return null;
	}
};

const ACCOUNT_CHANNEL_NAME_PREFIX = 'private-account';
const WORKFLOW_CHANNEL_NAME_PREFIX = 'private-workflow';
const ALERTS_CHANNEL_NAME = 'private-alerts';
const IDEA_CHANNEL_NAME_PREFIX = 'private-idea';
const getChannelNameForAccount = (accountId) => `${ACCOUNT_CHANNEL_NAME_PREFIX}-${accountId}`;
const getChannelNameForWorkflow = (workflowId) => `${WORKFLOW_CHANNEL_NAME_PREFIX}-${workflowId}`;
const getChannelNameForAlerts = (accountId, userId) => `${ALERTS_CHANNEL_NAME}-${accountId}-${userId}`;
const getChannelNameForIdea = (ideaId) => `${IDEA_CHANNEL_NAME_PREFIX}-${ideaId}`;

export const PUSHER_USAGE_TYPES = Object.freeze({
	ACCOUNT: Symbol('account'),
	WORKFLOW: Symbol('workflow'),
	ALERT: Symbol('alert'),
	IDEA: Symbol('idea'),
});

/* Public */

const pusherService = {
	pusherInstance: null,
	accountChannel: null,
	workflowChannels: [],
	alertChannel: null,
	ideaChannel: null,
	subscriptionsQueue: [],
	eventsQueue: [],
	accountId: null,
	userId: null,
	token: null,
	setPusherForUser(token, accountId, userId) {
		applyFn(() => {
			this.userId = userId;
			this.accountId = accountId;
			this.token = token;
			this.pusherInstance = new Pusher(VUE_APP_PUSHER_API_KEY, {
				cluster: VUE_APP_PUSHER_CLUSTER,
				channelAuthorization: {
					endpoint: VUE_APP_PUSHER_AUTH_ENDPOINT,
					headersProvider: () => ({
						Authorization: tokenStructure(this.token),
					}),
				},
			});
			this.subscriptionsQueue.forEach((subscription) => this.handleSubscription(subscription));
			this.subscriptionsQueue = [];
		});
	},
	refreshPusherToken(token) {
		this.token = token;
	},
	handleSubscription(subscription) {
		const { type: subscriptionType } = subscription;
		if (subscriptionType === PUSHER_USAGE_TYPES.WORKFLOW) {
			this.subscribeToWorkflow(subscription.id);
		} else if (subscriptionType === PUSHER_USAGE_TYPES.ALERT) {
			this.subscribeToAlerts();
		} else if (subscriptionType === PUSHER_USAGE_TYPES.IDEA) {
			this.subscribeToIdea(subscription.id);
		} else if (subscriptionType === PUSHER_USAGE_TYPES.ACCOUNT) {
			this.subscribeToAccount();
		}
	},
	subscribeToAccount() {
		applyFn(() => {
			if (!this.pusherInstance) {
				this.subscriptionsQueue.push({ type: PUSHER_USAGE_TYPES.ACCOUNT });
				return;
			}
			this.accountChannel = this.pusherInstance.subscribe(getChannelNameForAccount(this.accountId));
			// handle and remove account type events
			this.handleEventsForUsageType(PUSHER_USAGE_TYPES.ACCOUNT);
		});
	},
	subscribeToWorkflow(workflowId) {
		applyFn(() => {
			if (!this.pusherInstance) {
				this.subscriptionsQueue.push({ id: workflowId, type: PUSHER_USAGE_TYPES.WORKFLOW });
				return;
			}
			this.workflowChannels.push({ id: workflowId, channel: this.pusherInstance.subscribe(getChannelNameForWorkflow(workflowId)) });
			// handle and remove workflow type events
			this.handleEventsForUsageType(PUSHER_USAGE_TYPES.WORKFLOW, workflowId);
		});
	},
	subscribeToAlerts() {
		applyFn(() => {
			if (!this.pusherInstance) {
				this.subscriptionsQueue.push({ type: PUSHER_USAGE_TYPES.ALERT });
				return;
			}
			this.alertChannel = this.pusherInstance.subscribe(getChannelNameForAlerts(this.accountId, this.userId));
			// handle and remove alert type events
			this.handleEventsForUsageType(PUSHER_USAGE_TYPES.ALERT);
		});
	},
	subscribeToIdea(ideaId) {
		applyFn(() => {
			if (!this.pusherInstance) {
				this.subscriptionsQueue.push({ id: ideaId, type: PUSHER_USAGE_TYPES.IDEA });
				return;
			}
			this.ideaChannel = this.pusherInstance.subscribe(getChannelNameForIdea(ideaId));
			// handle and remove idea type events
			this.handleEventsForUsageType(PUSHER_USAGE_TYPES.IDEA);
		});
	},
	handleEventsForUsageType(type, channelId) {
		applyFn(() => {
			this.eventsQueue.filter((e) => e.type === type).forEach((args) => this.bindEvent({ ...args, id: channelId }));
			this.eventsQueue = this.eventsQueue.filter((e) => e.type !== type);
		});
	},
	unsubscribeFromChannel(channel, clearingPusher) {
		applyFn(() => {
			if (channel) {
				channel.unbind(); // unbind from all events in the channel
				if (clearingPusher) {
					// to prevent error message from pusher that webSocket is already in CLOSING or CLOSED state
					// seems like when being called from clearPusher, pusherInstance.disconnect is happening before pusherInstance.unsubscribe has a chance to end
					return;
				}
				this.pusherInstance.unsubscribe(channel.name);
			}
		});
	},
	unsubscribeFromWorkflow(clearingPusher, id = null) {
		applyFn(() => {
			if (id) {
				this.unsubscribeFromChannel(this.workflowChannels.find((item) => item.id === id)?.channel, clearingPusher);
			} else {
				this.workflowChannels.map((item) => this.unsubscribeFromChannel(item.channel, clearingPusher));
			}
		});
	},
	unsubscribeFromAlert(clearingPusher) {
		applyFn(() => {
			this.unsubscribeFromChannel(this.alertChannel, clearingPusher);
		});
	},
	unsubscribeFromIdea(clearingPusher) {
		applyFn(() => {
			this.unsubscribeFromChannel(this.ideaChannel, clearingPusher);
		});
	},
	unsubscribeFromAccount(clearingPusher) {
		applyFn(() => {
			this.unsubscribeFromChannel(this.accountChannel, clearingPusher);
		});
	},
	clearPusher() {
		applyFn(() => {
			this.userId = null;
			this.accountId = null;
			this.clearWorkflowChannel(true);
			this.clearAlertsChannel(true);
			this.clearIdeaChannel(true);
			this.clearAccountChannel(true);
			if (this.pusherInstance) {
				this.pusherInstance.disconnect();
				this.pusherInstance = null;
			}
		});
	},
	clearWorkflowChannel(clearingPusher, flowId = null) {
		applyFn(() => {
			this.unsubscribeFromWorkflow(clearingPusher, flowId);

			this.workflowChannels = flowId ? this.workflowChannels.filter((item) => item.id === flowId) : [];

			const relevantWorkflow = (item) =>
				flowId ? item.type === PUSHER_USAGE_TYPES.WORKFLOW && item.id === flowId : item.type === PUSHER_USAGE_TYPES.WORKFLOW;
			this.subscriptionsQueue = this.subscriptionsQueue.filter((i) => !relevantWorkflow(i)); // remove workflow events from queue
			this.eventsQueue = this.eventsQueue.filter((e) => !relevantWorkflow(e)); // remove workflow events from queue
		});
	},
	clearAlertsChannel(clearingPusher) {
		applyFn(() => {
			this.unsubscribeFromAlert(clearingPusher);
			this.alertChannel = null;
			this.subscriptionsQueue = this.subscriptionsQueue.filter((i) => i.type !== PUSHER_USAGE_TYPES.ALERT); // remove alerts events from queue
			this.eventsQueue = this.eventsQueue.filter((e) => e.type !== PUSHER_USAGE_TYPES.ALERT); // remove alerts events from queue
		});
	},
	clearIdeaChannel(clearingPusher) {
		applyFn(() => {
			this.unsubscribeFromIdea(clearingPusher);
			this.ideaChannel = null;
			this.subscriptionsQueue = this.subscriptionsQueue.filter((i) => i.type !== PUSHER_USAGE_TYPES.IDEA); // remove idea events from queue
			this.eventsQueue = this.eventsQueue.filter((e) => e.type !== PUSHER_USAGE_TYPES.IDEA); // remove idea events from queue
		});
	},
	clearAccountChannel(clearingPusher) {
		applyFn(() => {
			this.unsubscribeFromAccount(clearingPusher);
			this.accountChannel = null;
			this.subscriptionsQueue = this.subscriptionsQueue.filter((i) => i.type !== PUSHER_USAGE_TYPES.ACCOUNT); // remove account events from queue
			this.eventsQueue = this.eventsQueue.filter((e) => e.type !== PUSHER_USAGE_TYPES.ACCOUNT); // remove account events from queue
		});
	},
	listenToEvents(events, channelId = null) {
		applyFn(() => {
			events.forEach(({ name, callback, type }) => {
				const channel = this.getChannelForType(type, channelId);
				if (!channel) {
					this.eventsQueue.push({ name, callback, type });
					return;
				}
				this.bindToChannel(name, callback, channel);
			});
		});
	},
	bindToChannel(eventName, eventCallback, channel) {
		applyFn(() => {
			channel.bind(eventName, eventCallback);
		});
	},
	createPusherEventObj: (name, callback, type) => ({ name, callback, type }),
	getChannelForType(channelType, id) {
		const CHANNEL_SWITCHER = {
			[PUSHER_USAGE_TYPES.WORKFLOW]: () => this.workflowChannels.find((item) => item.id === id)?.channel,
			[PUSHER_USAGE_TYPES.ALERT]: () => this.alertChannel,
			[PUSHER_USAGE_TYPES.IDEA]: () => this.ideaChannel,
			[PUSHER_USAGE_TYPES.ACCOUNT]: () => this.accountChannel,
		};
		return CHANNEL_SWITCHER[channelType]();
	},
	bindEvent({ name, callback, type, id }) {
		const channel = this.getChannelForType(type, id);
		this.bindToChannel(name, callback, channel);
	},
};

export default pusherService;
